import { useMemo, useContext, useEffect, useState, useRef, forwardRef, Ref } from 'react';
import _ from "lodash";
import { Paper, Typography, Select, MenuItem, Chip, TablePagination, Switch, FormControlLabel } from '@material-ui/core';
import MaterialTable, { MTableToolbar } from 'material-table';
import { UserContext } from '../../../../contexts';
import { TablesContext, TableValueType } from '../TablesContext';
import AddBox from '@material-ui/icons/AddBox';
import ArrowDownward from '@material-ui/icons/ArrowDownward';
import Check from '@material-ui/icons/Check';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Clear from '@material-ui/icons/Clear';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import Edit from '@material-ui/icons/Edit';
import FilterList from '@material-ui/icons/FilterList';
import FirstPage from '@material-ui/icons/FirstPage';
import LastPage from '@material-ui/icons/LastPage';
import Remove from '@material-ui/icons/Remove';
import SaveAlt from '@material-ui/icons/SaveAlt';
import Search from '@material-ui/icons/Search';
import ViewColumn from '@material-ui/icons/ViewColumn';
import { makeStyles } from '@material-ui/core/styles';
import { SapTableNames, ApplicationRoles } from '../../../../../helpers';
import { DbType } from '../../../../../helpers/Common';

const useStyles = makeStyles((theme) => ({
    root: {
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        margin: 4,
        "&& th": {
            backgroundColor: "red"
        }
    },
    showObsolete: {
        marginLeft: theme.spacing(1)
    },
    notable: {
        padding: "16px"
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap'
    },
    chip: {
        margin: 2
    }
}));

const tableIcons = {
    Add: forwardRef((props, ref: Ref<SVGSVGElement>) => <AddBox {...props} ref={ref} />),
    Check: forwardRef((props, ref: Ref<SVGSVGElement>) => <Check {...props} ref={ref} />),
    Clear: forwardRef((props, ref: Ref<SVGSVGElement>) => <Clear {...props} ref={ref} />),
    Delete: forwardRef((props, ref: Ref<SVGSVGElement>) => <DeleteOutline {...props} ref={ref} />),
    DetailPanel: forwardRef((props, ref: Ref<SVGSVGElement>) => <ChevronRight {...props} ref={ref} />),
    Edit: forwardRef((props, ref: Ref<SVGSVGElement>) => <Edit {...props} ref={ref} />),
    Export: forwardRef((props, ref: Ref<SVGSVGElement>) => <SaveAlt {...props} ref={ref} />),
    Filter: forwardRef((props, ref: Ref<SVGSVGElement>) => <FilterList {...props} ref={ref} />),
    FirstPage: forwardRef((props, ref: Ref<SVGSVGElement>) => <FirstPage {...props} ref={ref} />),
    LastPage: forwardRef((props, ref: Ref<SVGSVGElement>) => <LastPage {...props} ref={ref} />),
    NextPage: forwardRef((props, ref: Ref<SVGSVGElement>) => <ChevronRight {...props} ref={ref} />),
    PreviousPage: forwardRef((props, ref: Ref<SVGSVGElement>) => <ChevronLeft {...props} ref={ref} />),
    ResetSearch: forwardRef((props, ref: Ref<SVGSVGElement>) => <Clear {...props} ref={ref} />),
    Search: forwardRef((props, ref: Ref<SVGSVGElement>) => <Search {...props} ref={ref} />),
    SortArrow: forwardRef((props, ref: Ref<SVGSVGElement>) => <ArrowDownward {...props} ref={ref} />),
    ThirdStateCheck: forwardRef((props, ref: Ref<SVGSVGElement>) => <Remove {...props} ref={ref} />),
    ViewColumn: forwardRef((props, ref: Ref<SVGSVGElement>) => <ViewColumn {...props} ref={ref} />)
};

export const TableValueList = (props) => {
    const classes: any = useStyles();

    const {
        isUserInRole,
        isUserInRoles,
    } = useContext(UserContext);

    const {
        state,
        isLoadingTableValues,
        addTableValue,
        updateTableValue,
        deleteTableValue
    } = useContext(TablesContext);

    const {
        tables,
        relatedTable,
        selectedTableId
    } = state;

    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [searchText, setSearchText] = useState(null);

    const tableWithRelatedEditable = [
        SapTableNames.ResponsibilityAreas,
        SapTableNames.CashManagementGroups,
        SapTableNames.AccountAssignmentGroup,
        SapTableNames.CustomerGroups6,
        SapTableNames.CustomerGroups10,
    ];

    const getCurrentTableName = () => {
        if (selectedTableId) {
            const table = tables.find(x => x.tableDefinitionId === selectedTableId);
            return table.name;
        }
        else
            return "No table selected";
    };

    const [showObsolete, setShowObsolete] = useState(false);
    const obsoleteSwitchHandler = (show) => {
        tableRef.current.dataManager.changeRowEditing(false);
        setPageIndex(0);
        setShowObsolete(show);
        setSearchText("");
    };



    const selectedTable = _.find(state.tables, x => x.tableDefinitionId === state.selectedTableId);
    const selectedTableColumns = [
        {
            title: "Related Value",
            field: "relatedValueIds",
            filtering: false,
            searchable: true,
            customFilterAndSearch: () => true,
            initialEditValue: "",
            hidden: true,
            width: "30%",
            render: (rowData) => {
                if (!rowData.relatedTable) {
                    return <Typography>&nbsp;</Typography>;
                }

                const childValues = props.dbType === DbType.ECC
                    ? rowData.relatedTable?.childValues_ECC
                    : rowData.relatedTable?.childValues_S4;

                return (
                    <div className={classes.chips}>
                        {_.map(rowData.relatedValueIds, (id: number, index: number) => {
                            const tableValue = _.find(childValues, x => x.tableValueId === id);

                            if (!!tableValue && !tableValue.isObsolete) {
                                return (
                                    <Chip
                                        key={index}
                                        label={tableValue.externalKey + " - " + tableValue.value}
                                        className={classes.chip}
                                    />
                                );
                            }
                        })}
                    </div>
                );
            },
            editComponent: (props) => {
                const { rowData } = props;
                const { data } = props.columnDef.lookup;
                const table = state.tables.find(x => x.tableDefinitionId === rowData.tableDefinitionId);

                return (
                    <>
                        {
                            tableWithRelatedEditable.find(x => table.name === x) ?
                            <>
                                {
                                    data &&
                                    data.children.length > 0 ?
                                    <Select
                                        className={classes.select}
                                        value={props.value || props.columnDef.initialEditValue}
                                        multiple={Array.isArray(props.columnDef.initialEditValue)}
                                        renderValue={(selected: any): JSX.Element => {
                                            if (!Array.isArray(selected))
                                                selected = [selected];

                                            return (
                                                <div className={classes.chips}>
                                                    {
                                                        selected.map((id, index): JSX.Element => {
                                                            const child = data.children.find(x => x.valueId === id);

                                                            return (
                                                                child ?
                                                                <Chip
                                                                    key={index}
                                                                    label={child.text}
                                                                    className={classes.chip}
                                                                /> :
                                                                null
                                                            );
                                                        })
                                                    }
                                                </div>
                                            )
                                        }}
                                        onChange={(e) => {
                                            props.onChange(e.target.value);
                                        }}
                                    >
                                        {
                                            data &&
                                            data.children.length > 0 &&
                                            data.children.map((child, index: number): JSX.Element =>
                                                <MenuItem
                                                    key={index}
                                                    value={child.valueId}
                                                >
                                                    {child.text}
                                                </MenuItem>
                                            )
                                        }
                                    </Select> :
                                    <Typography>
                                        No values available
                                    </Typography>
                                }
                            </> :
                            <>
                                <div className={classes.chips}>
                                    {props.value.map((id, index: number): JSX.Element => {
                                        const child = data.children.find(x => x.valueId === id);

                                        return (
                                            child ?
                                                <Chip
                                                    key={index}
                                                    label={child.text}
                                                    className={classes.chip}
                                                /> :
                                                null
                                        );
                                    })}
                                </div>
                            </>
                        }
                    </>
                );
            }
        },
        {
            title: "",
            searchable: false,
            hidden: props.tableValueType === TableValueType.Sap,
            width: "30%"
        },
        {
            title: "External Key",
            field: "externalKey",
            filtering: false,
            hidden: props.tableValueType !== TableValueType.Sap,
            editable: props.tableValueType === TableValueType.Sap ? "never" : "always",
            width: "25%"
        },
        {
            title: "Description",
            field: "value",
            filtering: false,
            editable: props.tableValueType === TableValueType.Sap ? "never" : "always",
            width: props.tableValueType === TableValueType.Sap ? "25%" : "80%"
        },
        {
            title: "Is Obsolete",
            field: "isObsolete",
            searchable: false,
            filtering: false,
            type: "boolean",
            headerStyle: { textAlign: "center" },
            cellStyle: { padding: 10, textAlign: "center" },
            width: "15%"
        }
    ];

    const tableColumns = useMemo(() => {
        const __cols = [].concat(selectedTableColumns);

        if (!state.isLoadingTableValues && !!state.selectedTableId && state.tables?.length > 0) {
            const hasRelatedTables = selectedTable.relatedTable !== null;
            const hideRltdForThese = [
                "Divisions",
                "Account Assignment Groups",
                "Cash Management Groups"
            ];
            const forceHideRltdColumn = !!_.find(hideRltdForThese, x => selectedTable.name === x);

            const columnRltdId = _.find(__cols, x => x.field === "relatedValueIds");
            columnRltdId.hidden = !hasRelatedTables || !!forceHideRltdColumn;
            columnRltdId.initialEditValue = selectedTable.hasMultipleRelatedValues ? [] : '';

            const columnDummy1 = __cols[1];
            columnDummy1.hidden = (hasRelatedTables && !forceHideRltdColumn) || props.tableValueType !== TableValueType.Sap;

            if (hasRelatedTables && relatedTable) {
                columnRltdId.title = relatedTable.name;

                const data = {
                    tableId: relatedTable.tableDefinitionId,
                    name: relatedTable.name,
                    children: []
                };

                const relatedTableChildValues = props.dbType === DbType.ECC
                    ? relatedTable?.childValues_ECC
                    : relatedTable?.childValues_S4;

                relatedTableChildValues
                    .filter(x => !x.isObsolete)
                    .forEach(x => {
                        data.children.push({
                            valueId: x.tableValueId,
                            value: x.value,
                            text:
                                relatedTable.valueType === TableValueType.Sap ?
                                `${x.externalKey} - ${x.value}` :
                                x.value
                        });
                    });

                columnRltdId.lookup = { data };
            }

            const columnExtKey = __cols.find(x => x.field === "externalKey");
            columnExtKey.title = selectedTable.name;
            columnExtKey.emptyValue = "@LOCAL";
        }

        return __cols;
    }, [
        state.tables,
        state.selectedTableId,
        state.tableValues,
        state.relatedTable
    ]);

    const [tableState, setTableState] = useState({ data: [] });

    const isProcessOwner = isUserInRole(ApplicationRoles.ProcessOwner) &&
        !isUserInRoles([ApplicationRoles.Administrator, ApplicationRoles.DataQuality]);

    useEffect(() => {
        if (!state.isLoadingTableValues && !!state.selectedTableId) {
            setTableState(prevState => {
                const newData =
                    selectedTable &&
                    (
                        selectedTable.relatedTable === null ||
                        relatedTable
                    ) ?
                    [].concat(state.tableValues) :
                    [];

                newData.forEach(x => x.relatedTable = relatedTable);

                if (selectedTable.name === SapTableNames.InfoTypes){
                    newData.forEach(x =>
                        x.externalKey = x.externalKey.split("_")[0]
                    );
                }

                return { ...prevState, data: newData };
            });
        }
    }, [state.tables, state.selectedTableId, state.tableValues, state.relatedTable]);

    useEffect(() => {
        if (state.selectedTableId) {
            tableRef.current.dataManager.changeSearchText("");
            tableRef.current.dataManager.changeApplySearch(false);
            tableRef.current.dataManager.changeRowEditing(false);
            setPageIndex(0);
            setShowObsolete(false);
            setSearchText(null);
        }
    }, [state.selectedTableId])

    useEffect(() => {
        if (props.onLoadMore)
            props.onLoadMore(state.selectedTableId, searchText, showObsolete, pageIndex, pageSize);
    }, [state.selectedTableId, searchText, showObsolete, pageIndex, pageSize]);

    const tableRef = useRef(null)

    if (!selectedTable) {
        return (
            <Paper className={classes.notable}>
                <Typography variant="h6">
                    No table selected
                </Typography>
                <Typography variant="body1" style={{ marginTop: 8 }}>
                    Select a table from the list to see what values are stored
                </Typography>
            </Paper>
        );
    }

    return (
        <MaterialTable
            tableRef={tableRef}
            isLoading={isLoadingTableValues}
            title={getCurrentTableName()}
            icons={tableIcons}
            columns={tableColumns}
            data={tableState.data}
            options={{
                search: true,
                sorting: false,
                paging: true,
                filtering: false,
                debounceInterval: 1000,
                pageSize: pageSize,
                pageSizeOptions: [20, 40, 60],
            }}
            onSearchChange={text => {
                setPageIndex(0);
                setSearchText(text);
            }}
            editable={isProcessOwner &&
                props.tableValueType === TableValueType.Sap &&
                getCurrentTableName() !== SapTableNames.ResponsibilityAreas &&
                getCurrentTableName() !== SapTableNames.CustomerGroups6 &&
                getCurrentTableName() !== SapTableNames.CustomerGroups10 ?
                null :
                {
                    onRowAdd:
                        props.tableValueType === TableValueType.Sap ?
                        null :
                        async (newData) => {
                            await addTableValue(
                                {
                                    ...newData,
                                    tableDefinitionId: selectedTable.tableDefinitionId,
                                    relatedValueIds:
                                        selectedTable.relatedTable ? (
                                            Array.isArray(newData.relatedValueIds) ?
                                                newData.relatedValueIds :
                                                [newData.relatedValueIds]
                                        ) : null,
                                    externalKey:
                                        props.tableValueType === TableValueType.Local ?
                                            "@LOCAL" :
                                            newData.externalKey,
                                    dbType: props.dbType
                                }
                            );
                        },
                    onRowUpdate: async (newData, oldData) => {
                        await updateTableValue(
                            {
                                ...newData,
                                tableDefinitionId: selectedTable.tableDefinitionId,
                                relatedValueIds:
                                    selectedTable.relatedTable ?
                                        (
                                            Array.isArray(newData.relatedValueIds) ?
                                                newData.relatedValueIds :
                                                [newData.relatedValueIds]
                                        ) :
                                        null,
                                dbType: props.dbType
                            }
                        );
                    },
                    onRowDelete:
                        props.tableValueType === TableValueType.Sap ?
                            null :
                            async (oldData) => {
                                await deleteTableValue(oldData.tableValueId, props.dbType);
                            }
                }
            }
            components={{
                Toolbar: (props): JSX.Element =>
                    <div>
                        <MTableToolbar {...props} />
                        <FormControlLabel
                            className={classes.showObsolete}
                            label="Show obsolete values"
                            control={
                                <Switch
                                    checked={showObsolete}
                                    color="primary"
                                    onChange={(e) => obsoleteSwitchHandler(e.target.checked)}
                                />
                            }
                        />
                    </div>,
                Pagination: (props): JSX.Element =>
                    <TablePagination
                        rowsPerPageOptions={[20, 40, 60]}
                        count={state.totalValues}
                        rowsPerPage={pageSize}
                        page={pageIndex}
                        SelectProps={{
                            inputProps: { 'aria-label': 'rows per page' },
                            native: true,
                        }}
                        onPageChange={(e, page) => {
                            setPageIndex(page);
                            tableRef.current.dataManager.changeCurrentPage(page);

                        }}
                        onRowsPerPageChange={e => {
                            const newPageSize = parseInt(e.target.value, 10);
                            setPageIndex(0);
                            setPageSize(newPageSize);
                            tableRef.current.dataManager.changePageSize(newPageSize);
                        }}
                    />
            }}
        />
    );
}